import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from 'semantic-ui-react'
import { useDebounce } from 'use-lodash-debounce'
import { useParams } from 'react-router-dom'
import { useAuth } from '~/routes/components/AuthProvider'
import TableComponent from '~/components/TableComponent'
import Layout from '~/components/Layout'
import StockExChangeService from '~/api/services/stockExchange'
import { handleErrors } from '~/utils/helpers'

const defaultFilter = {
  page: 0,
  search: '',
  rows: 10
}

interface FarmersTradingTableProps {
  farmersTrading: any
  farmerGroupId: string
}

const FarmersTradingTable: React.FC<FarmersTradingTableProps> = (props) => {
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const { id } = useParams()
  const [dataFiltered, setDataFiltered] = useState(defaultFilter)
  const [farmersTrading, setFarmersTrading] = useState(null)
  const [farmerGroupId, setFarmerGroupId] = useState(null)
  const [search, setSearch] = useState('')
  const { systemData } = useAuth()
  const debouncedValue = useDebounce(search, 300)
  const { amountUnit } = systemData

  const defalutTableHeaders = [
    {
      name: t('Supplier No.'),
      key: 'supplierNo',
      sorted: 'desc'
    },
    {
      name: t('Company name'),
      key: 'companyName'
    },
    {
      name: t('Total amount ({{amountUnit}} / year)', { amountUnit }),
      key: 'amount',
      type: 'number'
    },
    {
      name: t('Trading amount ({{amountUnit}} / year)', { amountUnit }),
      key: 'tradingAmount',
      type: 'number'
    },
    {
      name: t('Average trading amount ({{amountUnit}} / month)', { amountUnit }),
      key: 'monthlyTradingAmount',
      type: 'number'
    }
  ]

  const [tableColumnHeaders, setTableColumnHeaders] = useState(defalutTableHeaders)

  useEffect(() => {
    if (farmersTrading && farmerGroupId === props.farmerGroupId) {
      fetchFilteredData()
    }
  }, [dataFiltered, props.farmerGroupId])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  useEffect(() => {
    setTableColumnHeaders(defalutTableHeaders)
  }, [t])

  useEffect(() => {
    setFarmerGroupId(props.farmerGroupId)
    setFarmersTrading(props.farmersTrading)
    setDataFiltered(defaultFilter)
    setSearch('')
    setTableColumnHeaders(defalutTableHeaders)
  }, [props.farmerGroupId, props.farmersTrading])

  async function fetchFilteredData() {
    setIsBusy(true)
    try {
      const response = await StockExChangeService.farmersTradingSlot(id, {
        ...dataFiltered,
        page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
        farmerGroupId: props.farmerGroupId
      })
      setFarmersTrading(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderTradingActivities() {
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      rows={farmersTrading?.rows || []}
      total={farmersTrading?.filtered || 0}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
    />
  }

  function renderSearch() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  return <Layout
    isBusy={isBusy}
    title={t('Farmers')}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    filters={renderSearch()}
  >
    {farmersTrading && <div>
      {renderTradingActivities()}
    </div>}
  </Layout>
}
export default FarmersTradingTable
